import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { GetEmployeeName } from "../../../Auth/Services/authService";
import AddMasterDataModal from "../Modals/AddMasterDataModal";
import Cookies from "js-cookie";
import {
  getFunctions,
  getGrades,
  getZones,
  getDepartments,
  getJobLocations,
  getRoleType,
  getJobCategory,
  getGenderType,
} from "../../../Auth/Services/metadataService";
import {
  addFunction,
  addGrade,
  addZone,
  addRoleType,
  addJobCategory,
  addDepartment,
  addJobLocation,
  addGenderType
} from "../Services/masterdataService";
import NormalAlert from "../../../Alerts/NormalAlert";
import { Search } from "lucide-react";

const MasterDataTable = () => {
  const { type } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [addValue, setAddValue] = useState("");
  const [apiMessage, setApiMessage] = useState(null);
  const [dropdownList, setDropdownList] = useState([]);
  const [selectedParent, setSelectedParent] = useState("");
  const [alertData, setAlertData] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      let res = [];

      switch (type.toLowerCase()) {
        case "functions":
          res = await getFunctions();
          break;

        case "grades":
          res = await getGrades();
          break;

        case "zones":
          res = await getZones();
          break;

        // case "roletype":
        // case "role-type":
        // case "role-types":
        //   res = await getRoleType();
        //   break;

        case "jobcategory":
        case "job-category":
        case "job-categories":
          res = await getJobCategory();
          break;

        case "departments":
          const functions = await getFunctions();
          const deptResults = [];
          for (const func of functions) {
            const departments = await getDepartments(func.functionID);
            deptResults.push({
              parent: func.functionName,
              children: departments.map((d) => d.departmentName),
            });
          }
          res = deptResults;
          break;

        case "joblocations":
        case "job-location":
        case "job-locations":
          const zones = await getZones();
          const locResults = [];
          for (const z of zones) {
            const locations = await getJobLocations(z.zoneID);
            locResults.push({
              parent: z.zoneName,
              children: locations.map((l) => l.jobLocation),
            });
          }
          res = locResults;
          break;
        
        case "gender-preference":
        case "genderpreferences":
        case "gender-preferences":
          res = await getGenderType();
          break;

        default:
          res = [];
      }

      setData(res);
    } catch (error) {
      const safeMessage =
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";

      setAlertData({
        type: "fail",
        message: safeMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [type]);

  useEffect(() => {
    const checkRole = async () => {
      const token = Cookies.get("employeeToken");

      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await GetEmployeeName(token);
        const userRole = response?.data?.employeeRole;

        if (userRole !== "Admin Employee") {
          navigate("/Dashboard");
        }
      } catch (err) {
        navigate("/Dashboard");
      }
    };

    checkRole();
  }, []);

  const handleAdd = async () => {
    setAddValue("");
    setSelectedParent("");
    setApiMessage(null);

    const t = type.toLowerCase();

    if (t === "departments") {
      const funcs = await getFunctions();
      setDropdownList(funcs);
    }

    if (t === "joblocations" || t === "job-location" || t === "job-locations") {
      const zones = await getZones();
      setDropdownList(zones);
    }

    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!addValue.trim()) {
      setAlertData({
        type: "error",
        message: "Please enter a value.",
      });
      return;
    }

    try {
      let response;
      const t = type.toLowerCase();

      switch (t) {
        case "functions":
          response = await addFunction(addValue);
          break;

        case "grades":
          response = await addGrade(addValue);
          break;

        case "zones":
          response = await addZone(addValue);
          break;

        case "jobcategory":
        case "job-category":
        case "job-categories":
          response = await addJobCategory(addValue);
          break;

        case "departments":
          if (!selectedParent) {
            setAlertData({
              type: "error",
              message: "Please select a Function.",
            });
            return;
          }
          response = await addDepartment(selectedParent, addValue);
          break;

        case "joblocations":
        case "job-location":
        case "job-locations":
          if (!selectedParent) {
            setAlertData({
              type: "error",
              message: "Please select a Zone.",
            });
            return;
          }
          response = await addJobLocation(selectedParent, addValue);
          break;
        
        case "gender-preference":
        case "genderpreferences":
        case "gender-preferences":
          response = await addGenderType(addValue);
          break;


        default:
          setAlertData({
            type: "error",
            message: "Add option not implemented.",
          });
          return;
      }

      if (response?.data?.status === "Success") {
        setShowModal(false);

        setAlertData({
          type: "success",
          message: response.data.message,
        });

        fetchData(); // refresh table
      } else {
        setAlertData({
          type: "error",
          message: response?.data?.message || "Something went wrong",
        });
      }
    } catch (err) {
      setAlertData({
        type: "error",
        message: err.response?.data?.message || "Something went wrong",
      });
      setShowModal(false);
    }
  };

  const filteredData = data.filter((item) => {
    if (
      item.functionName ||
      item.gradeName ||
      item.zoneName ||
      // item.roleType ||
      item.jobCategory ||
      item.genderType
    ) {
      const name =
        item.functionName ||
        item.gradeName ||
        item.zoneName ||
        // item.roleType ||
        item.jobCategory ||
        item.genderType;

      return name.toLowerCase().includes(searchTerm.toLowerCase());
    }

    if (item.parent) {
      return (
        item.parent.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.children.some((c) =>
          c.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      );
    }

    return false;
  });

  const displayTitle =
    type.charAt(0).toUpperCase() + type.slice(1).replace("-", " ");

  return (
    <>
      <div className="w-full bg-[#eef1f5] py-14 mt-12">
        <div className="max-w-[1240px] mx-auto">
          <div className="bg-white rounded-[16px] px-[25px] py-[20px] shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
            {/* HEADER */}
            <div className="w-full bg-master-header text-white rounded-[12px] px-3 py-2 flex justify-between items-center">
              <h3 className="font-bold text-[18px]">
                {displayTitle} Master Data
              </h3>

              <button
                onClick={() => navigate("/Admin/Master-Data")}
                className="text-black rounded-full px-4 py-1 text-[17px] font-semibold bg-white/20 transition"
              >
                ← Back
              </button>
            </div>

            {/* TOOLBAR */}
            <div className="w-full flex justify-between items-center flex-wrap gap-3 my-4">
              {/* Search */}
              <div className="flex items-center bg-white border border-gray-200 rounded-full shadow-sm max-w-[250px] w-full">
                <Search
                  size={18}
                  strokeWidth={1.8}
                  className="text-[#6b7280] shrink-0 ml-5"
                />
                <input
                  type="text"
                  className="flex-1 border-0 focus:outline-none rounded-full px-3 py-2 text-sm"
                  placeholder={`Search ${displayTitle.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Add Button */}
              <button
                onClick={handleAdd}
                className="bg-primary text-white text-[14px] font-semibold rounded-full px-5 py-2 transition"
              >
                + Add {displayTitle}
              </button>
            </div>

            {/* TABLE */}
            <div className="overflow-x-auto w-full">
              <table className="w-full border-collapse text-center">
                <thead>
                  <tr className="bg-[#f5f8ff] text-[#333] text-[13px] font-semibold uppercase">
                    <th className="py-[14px] px-[10px] border-b border-[#e0e0e0]">
                      #
                    </th>

                    <th className="py-[14px] px-[10px] border-b border-[#e0e0e0]">
                      {type.toLowerCase() === "departments"
                        ? "Function Name"
                        : type.toLowerCase() === "job-locations"
                          ? "Zone Name"
                          : `${displayTitle} Name`}
                    </th>

                    {(type.toLowerCase() === "departments" ||
                      type.toLowerCase() === "job-locations") && (
                      <th className="py-[14px] px-[10px] border-b border-[#e0e0e0]">
                        {type.toLowerCase() === "departments"
                          ? "Departments"
                          : "Job Locations"}
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {filteredData.length > 0 ? (
                    filteredData.map((item, index) =>
                      type.toLowerCase() === "departments" ||
                      type.toLowerCase() === "job-locations" ? (
                        <tr key={index}>
                          <td className="py-[10px] px-[10px] border-b border-gray-200">
                            {index + 1}
                          </td>

                          <td className="py-[10px] px-[10px] border-b border-gray-200">
                            {item.parent}
                          </td>

                          <td className="py-[10px] px-[10px] border-b border-gray-200">
                            <div className="flex flex-wrap gap-2 justify-center">
                              {item.children.map((child, i) => (
                                <span
                                  key={i}
                                  className="px-3 py-1 text-sm rounded-full border bg-gray-50"
                                >
                                  {child}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr key={index}>
                          <td className="py-[10px] px-[10px] border-b border-gray-200">
                            {index + 1}
                          </td>

                          <td className="py-[10px] px-[10px] border-b border-gray-200">
                            {item.functionName ||
                              item.gradeName ||
                              item.zoneName ||
                              // item.roleType ||
                              item.jobCategory ||
                              item.genderType}
                          </td>
                        </tr>
                      ),
                    )
                  ) : (
                    <tr>
                      <td colSpan="3" className="py-4 text-red-500">
                        No {displayTitle.toLowerCase()} found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <AddMasterDataModal
          show={showModal}
          onClose={() => setShowModal(false)}
          displayTitle={displayTitle}
          addValue={addValue}
          setAddValue={setAddValue}
          selectedParent={selectedParent}
          setSelectedParent={setSelectedParent}
          dropdownList={dropdownList}
          type={type}
          onSubmit={handleSubmit}
        />
      </div>

      {alertData && (
        <NormalAlert
          type={alertData.type}
          message={alertData.message}
          autoClose={2500}
          onClose={() => setAlertData(null)}
        />
      )}
    </>
  );
};

export default MasterDataTable;
